import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  X, 
  AlertCircle, 
  CheckCircle2, 
  ExternalLink,
  Mail,
  Calendar
} from 'lucide-react';
import { format } from 'date-fns';

interface EmailEvent {
  id: string;
  template_type: string;
  user_id: string;
  user_email: string;
  community_id: string;
  event_data: {
    email_action_type?: string;
    email_id?: string;
    sender_email?: string;
    template_variables?: Record<string, unknown>;
    error?: string;
    [key: string]: unknown;
  };
  email_sent: boolean;
  processed_at: string;
  created_at: string;
  processed_subject?: string;
  processed_html?: string;
  sender_email?: string;
  user_profile?: {
    firstname: string;
    lastname: string;
  };
}

interface EmailViewSheetProps {
  communityId: string;
}

export const EmailViewSheet: React.FC<EmailViewSheetProps> = ({ communityId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get('event_id');

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['email-event', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      
      const { data, error } = await supabase
        .from('email_events')
        .select(`
          *,
          user_profile:user_profiles!email_events_user_id_fkey(
            firstname,
            lastname
          )
        `)
        .eq('id', eventId)
        .eq('community_id', communityId)
        .single();
      
      if (error) throw error;
      return data as EmailEvent;
    },
    enabled: !!eventId && !!communityId
  });

  const handleClose = () => {
    searchParams.delete('event_id');
    setSearchParams(searchParams);
  };

  const getUserName = (event: EmailEvent) => {
    if (event.user_profile?.firstname || event.user_profile?.lastname) {
      return `${event.user_profile.firstname || ''} ${event.user_profile.lastname || ''}`.trim();
    }
    return event.user_email.split('@')[0];
  };

  const getUserInitials = (event: EmailEvent) => {
    const name = getUserName(event);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getSenderName = (senderEmail: string) => {
    // Extract name from "Name <email@domain.com>" format
    const match = senderEmail.match(/^(.*?)\s*<(.+)>$/);
    return match ? match[1] : senderEmail;
  };

  const getSenderInitials = (senderEmail: string) => {
    const name = getSenderName(senderEmail);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy \'at\' h:mm a');
  };

  const getTemplateLabel = (templateType: string): string => {
    const labels: Record<string, string> = {
      'email_confirmation': 'Email Confirmation',
      'password_reset': 'Password Reset',
      'welcome': 'Welcome',
      'business_registration': 'Business Registration',
      'business_approval': 'Business Approval',
      'business_listed': 'Business Listed',
      'pending_subscription_business_listed': 'Pending Subscription Business Listed',
      'review_notification': 'Review Notification',
      'offline_notification': 'Offline Notification',
      'job_proposal_notification': 'Job Proposal',
      'proposal_response_notification': 'Proposal Response',
      'admin_business_pending_approval': 'Admin: Pending Approval'
    };
    return labels[templateType] || templateType;
  };

  if (!eventId || !event) return null;

  return (
    <Sheet open={!!eventId} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>View Email</SheetTitle>
          <SheetDescription>
            View email details and content sent to community members
          </SheetDescription>
        </SheetHeader>
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 sm:p-6">
              <div className="text-destructive p-4 rounded-md bg-destructive/10">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>Error loading email details</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Email Header Section */}
              <div className="bg-muted/30 border-b">
                <div className="p-4 sm:p-6 space-y-4">
                  {/* Subject with Status Badge */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-0">
                      {event.processed_subject || 'No Subject'}
                    </h1>
                    {event.email_sent ? (
                      <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Sent
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Failed
                      </Badge>
                    )}
                  </div>

                  {/* From/To Section */}
                  <div className="space-y-3">
                    {/* From */}
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {event.sender_email ? getSenderInitials(event.sender_email) : 'SY'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-muted-foreground">From</div>
                        <div className="font-medium truncate">
                          {event.sender_email || 'System'}
                        </div>
                      </div>
                    </div>

                    {/* To */}
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-slate-100 text-slate-600 text-sm">
                          {getUserInitials(event)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-muted-foreground">To</div>
                        <div className="font-medium">{getUserName(event)}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {event.user_email}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date and Template Type */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(event.processed_at || event.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      <span>{getTemplateLabel(event.template_type)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs for Preview and Advanced */}
              <Tabs defaultValue="preview" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mt-4 mb-4">
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                  <TabsTrigger value="advanced">Advanced</TabsTrigger>
                </TabsList>

                {/* Preview Tab */}
                <TabsContent value="preview" className="mt-0">
                  {/* Email Body */}
                  {event.processed_html && (
                    <div className="bg-white">
                      <iframe
                        srcDoc={event.processed_html}
                        className="w-full min-h-[400px] sm:min-h-[500px] border-0"
                        title="Email Content"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  )}

                  {/* Error Message */}
                  {event.event_data.error && (
                    <div className="p-4 sm:p-6">
                      <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                          <div>
                            <p className="font-medium text-destructive">Failed to send email</p>
                            <p className="text-sm text-destructive/90 mt-1">
                              {event.event_data.error}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className=" bg-background border-t p-4 sm:p-6">
                    <div className="flex gap-2">
                      {event.processed_html && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const blob = new Blob([event.processed_html || ''], { type: 'text/html' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open in Browser
                        </Button>
                      )}
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleClose}
                        className="ml-auto"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Advanced Tab */}
                <TabsContent value="advanced" className="mt-0">
                  <div className="p-4 sm:p-6 space-y-4">
                    {/* Email ID */}
                    {event.event_data.email_id && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Email ID (Resend)</p>
                        <p className="font-mono text-xs bg-muted p-2 rounded break-all">
                          {event.event_data.email_id}
                        </p>
                      </div>
                    )}

                    {/* Event Data */}
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Event Data</p>
                      <pre className="text-[10px] bg-muted p-3 rounded overflow-x-auto leading-tight">
                        {JSON.stringify(event.event_data, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className=" bg-background border-t p-4 sm:p-6">
                    <div className="flex gap-2">
                      {event.processed_html && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const blob = new Blob([event.processed_html || ''], { type: 'text/html' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open in Browser
                        </Button>
                      )}
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleClose}
                        className="ml-auto"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
      </SheetContent>
    </Sheet>
  );
};